import { getHealth, URBANIK_RELEASE } from '@urbanik/api-client';
import { Button, InfoPanel, StatusBadge } from '@urbanik/ui';
import { CheckCircle2, Cloud, Download, MonitorSmartphone, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { apiBase } from './api-base';
import './version-updates.css';

function surfaceLabel(): string {
  if (window.urbanikPlatform?.runtime === 'electron') return 'Aplicación de escritorio';
  if (window.matchMedia?.('(display-mode: standalone)').matches) return 'PWA instalada';
  return 'Navegador web';
}

export function VersionUpdatesView() {
  const [apiVersion, setApiVersion] = useState<string | null>(null);
  const [apiAvailable, setApiAvailable] = useState(true);
  const [checking, setChecking] = useState(false);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let active = true;
    void getHealth(apiBase)
      .then((health) => {
        if (active) setApiVersion(health.version);
      })
      .catch(() => {
        if (active) setApiAvailable(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const synchronized = apiVersion === null || apiVersion === URBANIK_RELEASE.version;
  async function checkForUpdates() {
    setChecking(true);
    setNotice('');
    try {
      const registration = await navigator.serviceWorker?.getRegistration();
      await registration?.update();
      setNotice('Comprobación terminada. Ya utilizas la versión disponible más reciente.');
    } catch {
      setNotice('No se pudo comprobar la actualización. Se intentará nuevamente en segundo plano.');
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="version-page">
      <header className="version-hero">
        <div>
          <p className="kicker">INFORMACIÓN DE LA APLICACIÓN</p>
          <h1>Versión y novedades</h1>
          <p>Consulta qué versión estás usando y los cambios incorporados en URBANIK.</p>
        </div>
        <StatusBadge tone={synchronized ? 'success' : 'warning'}>
          {synchronized ? 'Aplicación actualizada' : 'Actualización pendiente'}
        </StatusBadge>
      </header>

      <section className="version-summary" aria-label="Versión instalada">
        <article>
          <CheckCircle2 aria-hidden="true" />
          <div>
            <span>Versión</span>
            <strong>{URBANIK_RELEASE.version}</strong>
          </div>
        </article>
        <article>
          <Download aria-hidden="true" />
          <div>
            <span>Release</span>
            <strong>{URBANIK_RELEASE.releaseId}</strong>
          </div>
        </article>
        <article>
          <MonitorSmartphone aria-hidden="true" />
          <div>
            <span>Plataforma</span>
            <strong>{surfaceLabel()}</strong>
          </div>
        </article>
        <article>
          <Cloud aria-hidden="true" />
          <div>
            <span>API</span>
            <strong>{apiAvailable ? (apiVersion ?? 'Verificando…') : 'No disponible'}</strong>
          </div>
        </article>
      </section>

      {!apiAvailable ? (
        <InfoPanel title="No pudimos verificar el servidor" tone="warning">
          La aplicación continuará comprobando la conexión. La información local de versión sigue
          disponible.
        </InfoPanel>
      ) : null}
      {!synchronized ? (
        <InfoPanel title="Hay componentes con versiones diferentes" tone="warning">
          Guarda cualquier formulario abierto y comprueba la actualización.
        </InfoPanel>
      ) : null}

      <section className="version-release-card">
        <header>
          <div>
            <p className="kicker">RELEASE {URBANIK_RELEASE.releaseId}</p>
            <h2>{URBANIK_RELEASE.title}</h2>
            <time dateTime={URBANIK_RELEASE.releasedAt}>8 de septiembre de 2026</time>
          </div>
          <Button
            type="button"
            icon={<RefreshCw aria-hidden="true" />}
            disabled={checking}
            onClick={() => void checkForUpdates()}
          >
            {checking ? 'Comprobando…' : 'Buscar actualización'}
          </Button>
        </header>
        {notice ? (
          <p className="version-update-notice" role="status">
            {notice}
          </p>
        ) : null}
        <div className="version-change-grid">
          {URBANIK_RELEASE.changes.map((group) => (
            <article key={group.title}>
              <h3>{group.title}</h3>
              <ul>
                {group.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
